/**
 * In-memory industry starter packs for the mock API client (tracker item 76).
 * Same rules as the backend where the UI depends on them: additive and
 * idempotent apply (an existing record is never modified and is reported as
 * skipped EXISTS / EXISTS_DELETED / ACCOUNT_MISSING), preview writes nothing,
 * every created record is flagged in a side list until Keep, and Remove deletes
 * only flagged records that are unused and unedited since seeding. The host
 * client supplies the lookups and creates records in its own stores.
 *
 * The packs here are small samples (not the backend's real content), and the
 * notification wording overrides are not simulated (count 0).
 */
import * as types from './types';

export class MockStarterError extends Error {
  constructor(public status: number, message: string, public code: string) {
    super(message);
  }
}

/** What the host client does for the mock starter packs. */
export interface MockStarterHost {
  accountId(tenantId: string, accountNumber: string): string | null;
  find(tenantId: string, type: types.StarterPlanEntityType, name: string): { id: string; deleted: boolean } | null;
  createCategory(tenantId: string, name: string): string;
  create(
    tenantId: string,
    type: Exclude<types.StarterEntityType, 'NOTIFICATION_TEMPLATE'>,
    definition: unknown,
    links: { categoryId?: string; revenueAccountId?: string; expenseAccountId?: string; priceChartId?: string; checklistTemplateId?: string }
  ): { id: string; updatedAt: string };
  /** Bring a soft-deleted item back with the pack's definition */
  restoreItem(
    tenantId: string,
    id: string,
    definition: types.StarterPackItem,
    links: { categoryId?: string; revenueAccountId?: string; expenseAccountId?: string; priceChartId?: string; checklistTemplateId?: string }
  ): { id: string; updatedAt: string };
  /** The record's current name and updatedAt, or null when it no longer exists */
  state(tenantId: string, type: types.StarterEntityType, id: string): { name: string; updatedAt: string } | null;
  isUsed(tenantId: string, type: types.StarterEntityType, id: string): boolean;
  remove(tenantId: string, type: types.StarterEntityType, id: string): void;
  audit(action: types.AuditAction, details: Record<string, unknown>): void;
}

interface SeededRow {
  id: string;
  tenantId: string;
  packKey: types.StarterPackKey;
  entityType: types.StarterEntityType;
  entityId: string;
  seededAt: string;
  /** An ITEM that a remove soft-deleted; kept so a later apply restores it */
  removedAt?: string;
}

const SERVICE = 'SERVICE';
const lead = (names: string[]) => names.map((name) => ({ name }));
const COMMON_LEADS = lead(['Referral', 'Website', 'Google', 'Door hanger', 'Yard sign', 'Repeat customer']);

function pack(p: Partial<types.StarterPack> & Pick<types.StarterPack, 'key' | 'title' | 'description'>): types.StarterPack {
  return {
    items: [],
    priceCharts: [],
    checklistTemplates: [],
    notificationOverrides: [],
    leadSources: COMMON_LEADS,
    recurringPresets: [],
    ...p,
  };
}

const item = (name: string, unitPrice: string, category: string, extra: Partial<types.StarterPackItem> = {}): types.StarterPackItem => ({
  name,
  itemType: SERVICE,
  unitPrice,
  unitOfMeasure: 'visit',
  taxable: false,
  category,
  revenueAccountNumber: '4000',
  defaultDurationMinutes: 30,
  ...extra,
});

const weekly = (name: string, interval: number, day: number): types.StarterPackRecurringPreset => ({
  name, frequency: 'WEEKLY', interval, frequencyDetails: { daysOfWeek: [day] }, billingModel: 'PER_VISIT',
});

export const MOCK_STARTER_PACKS: types.StarterPack[] = [
  pack({
    key: 'lawn_care',
    title: 'Lawn care',
    description: 'Mowing, edging, trimming and fertilization, with a mowing price chart by lot size, visit checklists and mowing patterns.',
    items: [
      item('Mowing', '45.00', 'Lawn Maintenance', { priceChart: 'Mowing by Lot Size', checklistTemplate: 'Mowing Visit', defaultDurationMinutes: 45 }),
      item('Edging', '15.00', 'Lawn Maintenance', { defaultDurationMinutes: 15 }),
      item('Leaf Cleanup', '120.00', 'Seasonal Services', { defaultDurationMinutes: 90 }),
      item('Fertilization', '65.00', 'Lawn Treatment', { checklistTemplate: 'Fertilization Visit' }),
    ],
    priceCharts: [{
      name: 'Mowing by Lot Size', description: 'Mowing priced by lot size', unit: 'SQFT', pricingType: 'BAND_FLAT', overageRatePerUnit: '0.0050',
      tiers: [{ upToSize: '5000', price: '40.00' }, { upToSize: '10000', price: '55.00' }, { upToSize: '20000', price: '80.00' }],
    }],
    checklistTemplates: [
      { name: 'Mowing Visit', steps: [{ text: 'Clear debris from the lawn', required: true }, { text: 'Mow and edge', required: true }, { text: 'Blow off hard surfaces', required: false }] },
      { name: 'Fertilization Visit', steps: [{ text: 'Check the weather', required: true }, { text: 'Apply fertilizer', required: true }] },
    ],
    recurringPresets: [weekly('Weekly mowing', 1, 1), weekly('Biweekly mowing', 2, 1)],
  }),
  pack({
    key: 'pest_control',
    title: 'Pest control',
    description: 'General treatment, rodent exclusion, termite inspection and mosquito spray, with treatment checklists and quarterly visits.',
    items: [
      item('General Pest Treatment', '125.00', 'Pest Treatments', { checklistTemplate: 'Interior Treatment', defaultDurationMinutes: 60 }),
      item('Rodent Exclusion', '350.00', 'Pest Treatments', { defaultDurationMinutes: 180 }),
      item('Termite Inspection', '95.00', 'Inspections'),
      item('Mosquito Spray', '75.00', 'Pest Treatments'),
    ],
    checklistTemplates: [
      { name: 'Interior Treatment', steps: [{ text: 'Inspect entry points', required: true }, { text: 'Treat baseboards and corners', required: true }] },
    ],
    recurringPresets: [
      { name: 'Quarterly treatment', frequency: 'QUARTERLY', interval: 1, billingModel: 'PER_VISIT' },
      { name: 'Monthly mosquito', frequency: 'MONTHLY', interval: 1, frequencyDetails: { dayOfMonth: 1 }, billingModel: 'PER_VISIT' },
    ],
  }),
  pack({
    key: 'hvac',
    title: 'HVAC',
    description: 'Diagnostics, tune-ups, filter changes, refrigerant and labor, with tune-up and install checklists.',
    items: [
      item('Diagnostic Visit', '89.00', 'Service Calls', { taxable: true, defaultDurationMinutes: 60 }),
      item('System Tune-Up', '149.00', 'Maintenance', { taxable: true, checklistTemplate: 'Tune-Up', defaultDurationMinutes: 90 }),
      item('Filter Change', '35.00', 'Maintenance', { taxable: true, itemType: 'PRODUCT', unitOfMeasure: 'each', unitCost: '12.00', expenseAccountNumber: '5001', reorderPoint: '5', defaultDurationMinutes: undefined }),
      // Account 4100 is not in the mock chart of accounts: shows the ACCOUNT_MISSING skip
      item('Refrigerant (per lb)', '45.00', 'Parts', { itemType: 'PRODUCT', unitOfMeasure: 'lb', unitCost: '20.00', revenueAccountNumber: '4100', expenseAccountNumber: '5001', defaultDurationMinutes: undefined }),
    ],
    checklistTemplates: [
      { name: 'Tune-Up', steps: [{ text: 'Check the thermostat', required: true }, { text: 'Inspect the coils', required: true }, { text: 'Test the airflow', required: false }] },
    ],
    recurringPresets: [
      { name: 'Semiannual tune-up', frequency: 'MONTHLY', interval: 6, frequencyDetails: { dayOfMonth: 1 }, billingModel: 'PER_VISIT' },
    ],
  }),
  pack({
    key: 'cleaning',
    title: 'Cleaning',
    description: 'Standard, deep and move-out cleans, with a price chart by home size, room checklists and weekly to monthly patterns.',
    items: [
      item('Standard Clean', '120.00', 'Cleaning', { priceChart: 'Standard Clean by Home Size', checklistTemplate: 'Standard Room List', defaultDurationMinutes: 120 }),
      item('Deep Clean', '240.00', 'Cleaning', { defaultDurationMinutes: 240 }),
      item('Move-Out Clean', '300.00', 'Cleaning', { defaultDurationMinutes: 300 }),
      item('Window Cleaning', '80.00', 'Add-ons', { defaultDurationMinutes: 60 }),
    ],
    priceCharts: [{
      name: 'Standard Clean by Home Size', unit: 'SQFT', pricingType: 'BAND_FLAT', overageRatePerUnit: '0.0500',
      tiers: [{ upToSize: '1000', price: '100.00' }, { upToSize: '2000', price: '150.00' }, { upToSize: '3000', price: '210.00' }],
    }],
    checklistTemplates: [
      { name: 'Standard Room List', steps: [{ text: 'Kitchen', required: true }, { text: 'Bathrooms', required: true }, { text: 'Bedrooms', required: false }] },
    ],
    recurringPresets: [weekly('Weekly clean', 1, 1), weekly('Biweekly clean', 2, 1), { name: 'Monthly clean', frequency: 'MONTHLY', interval: 1, frequencyDetails: { dayOfMonth: 1 }, billingModel: 'PER_VISIT' }],
  }),
  pack({
    key: 'pool_service',
    title: 'Pool service',
    description: 'Weekly service, chemical balance, filter cleaning and seasonal opening and closing, with a weekly service checklist.',
    items: [
      item('Weekly Pool Service', '85.00', 'Pool Service', { checklistTemplate: 'Weekly Pool Visit', defaultDurationMinutes: 45 }),
      item('Chemical Balance', '40.00', 'Pool Service', { defaultDurationMinutes: 20 }),
      item('Filter Clean', '70.00', 'Pool Service'),
      item('Pool Opening', '250.00', 'Seasonal', { defaultDurationMinutes: 180 }),
    ],
    checklistTemplates: [
      { name: 'Weekly Pool Visit', steps: [{ text: 'Skim and brush', required: true }, { text: 'Test and balance chemicals', required: true }, { text: 'Empty baskets', required: false }] },
    ],
    recurringPresets: [weekly('Weekly pool service', 1, 3)],
  }),
];

/** Removal order: items first, so the charts and templates they used are free to go. */
const REMOVE_ORDER: types.StarterEntityType[] = ['ITEM', 'CHECKLIST_TEMPLATE', 'PRICE_CHART', 'LEAD_SOURCE', 'RECURRING_PRESET'];

export class MockStarter {
  private rows: SeededRow[] = [];
  private seq = 0;

  constructor(private readonly host: MockStarterHost) {}

  private pack(key: string): types.StarterPack {
    const found = MOCK_STARTER_PACKS.find((p) => p.key === key);
    if (!found) throw new MockStarterError(404, `Unknown starter pack '${key}'`, types.STARTER_ERROR_CODES.unknownPack);
    return found;
  }

  /** Flagged rows whose record still exists, with its current state. */
  private live(tenantId: string, packKey?: string) {
    return this.rows
      .filter((r) => r.tenantId === tenantId && !r.removedAt && (!packKey || r.packKey === packKey))
      .map((row) => ({ row, state: this.host.state(tenantId, row.entityType, row.entityId) }))
      .filter((x): x is { row: SeededRow; state: { name: string; updatedAt: string } } => x.state !== null);
  }

  listPacks(tenantId: string): types.StarterPackSummary[] {
    const live = this.live(tenantId);
    return MOCK_STARTER_PACKS.map((p) => ({
      key: p.key,
      title: p.title,
      description: p.description,
      counts: {
        items: p.items.length,
        priceCharts: p.priceCharts.length,
        checklistTemplates: p.checklistTemplates.length,
        notificationOverrides: p.notificationOverrides.length,
        leadSources: p.leadSources.length,
        recurringPresets: p.recurringPresets.length,
      },
      applied: live.some((x) => x.row.packKey === p.key),
      seededCount: live.filter((x) => x.row.packKey === p.key).length,
    }));
  }

  getPack(key: string): types.StarterPack {
    return JSON.parse(JSON.stringify(this.pack(key)));
  }

  private plan(tenantId: string, p: types.StarterPack): types.StarterPlanEntry[] {
    const entries: types.StarterPlanEntry[] = [];
    const add = (entityType: types.StarterPlanEntityType, name: string, reason: types.StarterSkipReason | null) =>
      entries.push({ entityType, name, action: reason ? 'skip' : 'create', reason });
    for (const name of new Set(p.items.map((i) => i.category))) {
      add('ITEM_CATEGORY', name, this.host.find(tenantId, 'ITEM_CATEGORY', name) ? 'EXISTS' : null);
    }
    for (const t of p.checklistTemplates) add('CHECKLIST_TEMPLATE', t.name, this.host.find(tenantId, 'CHECKLIST_TEMPLATE', t.name) ? 'EXISTS' : null);
    for (const c of p.priceCharts) add('PRICE_CHART', c.name, this.host.find(tenantId, 'PRICE_CHART', c.name) ? 'EXISTS' : null);
    for (const i of p.items) {
      const existing = this.host.find(tenantId, 'ITEM', i.name);
      const accountsOk = !!this.host.accountId(tenantId, i.revenueAccountNumber)
        && (!i.expenseAccountNumber || !!this.host.accountId(tenantId, i.expenseAccountNumber));
      const restorable = !!existing?.deleted && this.removedItemRow(tenantId, p.key, existing.id) !== undefined;
      if (restorable && accountsOk) entries.push({ entityType: 'ITEM', name: i.name, action: 'restore', reason: null });
      else {
        add('ITEM', i.name, existing
          ? (existing.deleted ? (restorable ? 'ACCOUNT_MISSING' : 'EXISTS_DELETED') : 'EXISTS')
          : accountsOk ? null : 'ACCOUNT_MISSING');
      }
    }
    for (const s of p.leadSources) add('LEAD_SOURCE', s.name, this.host.find(tenantId, 'LEAD_SOURCE', s.name) ? 'EXISTS' : null);
    for (const r of p.recurringPresets) add('RECURRING_PRESET', r.name, this.host.find(tenantId, 'RECURRING_PRESET', r.name) ? 'EXISTS' : null);
    return entries;
  }

  private removedItemRow(tenantId: string, packKey: string, itemId: string): SeededRow | undefined {
    return this.rows.find((r) => r.tenantId === tenantId && r.packKey === packKey && r.entityType === 'ITEM' && r.entityId === itemId && r.removedAt);
  }

  preview(tenantId: string, key: string): types.StarterPreviewResponse {
    const p = this.pack(key);
    const entries = this.plan(tenantId, p);
    return {
      packKey: p.key,
      willCreate: entries.filter((e) => e.action === 'create').length,
      willRestore: entries.filter((e) => e.action === 'restore').length,
      willSkip: entries.filter((e) => e.action === 'skip').length,
      entries,
    };
  }

  apply(tenantId: string, key: string): types.StarterApplyResponse {
    const p = this.pack(key);
    const plan = this.plan(tenantId, p);
    const creates = (type: types.StarterPlanEntityType, name: string) =>
      plan.some((e) => e.entityType === type && e.name === name && e.action === 'create');
    const ids = new Map<string, string>();
    const flag = (entityType: types.StarterEntityType, created: { id: string; updatedAt: string }) =>
      this.rows.push({ id: `mock-seeded-${++this.seq}`, tenantId, packKey: p.key, entityType, entityId: created.id, seededAt: created.updatedAt });
    const k = (type: string, name: string) => `${type}:${name}`;

    const categoryIds = new Map<string, string>();
    for (const i of p.items) {
      if (categoryIds.has(i.category)) continue;
      if (creates('ITEM_CATEGORY', i.category)) {
        const id = this.host.createCategory(tenantId, i.category);
        categoryIds.set(i.category, id);
        ids.set(k('ITEM_CATEGORY', i.category), id);
      } else {
        const existing = this.host.find(tenantId, 'ITEM_CATEGORY', i.category);
        if (existing) categoryIds.set(i.category, existing.id);
      }
    }
    const templateIds = new Map<string, string>();
    for (const t of p.checklistTemplates) {
      if (!creates('CHECKLIST_TEMPLATE', t.name)) continue;
      const created = this.host.create(tenantId, 'CHECKLIST_TEMPLATE', t, {});
      templateIds.set(t.name, created.id);
      ids.set(k('CHECKLIST_TEMPLATE', t.name), created.id);
      flag('CHECKLIST_TEMPLATE', created);
    }
    const chartIds = new Map<string, string>();
    for (const c of p.priceCharts) {
      if (!creates('PRICE_CHART', c.name)) continue;
      const created = this.host.create(tenantId, 'PRICE_CHART', c, {});
      chartIds.set(c.name, created.id);
      ids.set(k('PRICE_CHART', c.name), created.id);
      flag('PRICE_CHART', created);
    }
    for (const i of p.items) {
      const restores = plan.some((e) => e.entityType === 'ITEM' && e.name === i.name && e.action === 'restore');
      if (!creates('ITEM', i.name) && !restores) continue;
      const links = {
        categoryId: categoryIds.get(i.category),
        revenueAccountId: this.host.accountId(tenantId, i.revenueAccountNumber) ?? undefined,
        expenseAccountId: (i.expenseAccountNumber && this.host.accountId(tenantId, i.expenseAccountNumber)) || undefined,
        priceChartId: i.priceChart ? chartIds.get(i.priceChart) : undefined,
        checklistTemplateId: i.checklistTemplate ? templateIds.get(i.checklistTemplate) : undefined,
      };
      if (restores) {
        const existing = this.host.find(tenantId, 'ITEM', i.name) as { id: string };
        const restored = this.host.restoreItem(tenantId, existing.id, i, links);
        const row = this.removedItemRow(tenantId, p.key, existing.id) as SeededRow;
        row.removedAt = undefined;
        row.seededAt = restored.updatedAt;
        ids.set(k('ITEM', i.name), restored.id);
        continue;
      }
      const created = this.host.create(tenantId, 'ITEM', i, links);
      ids.set(k('ITEM', i.name), created.id);
      flag('ITEM', created);
    }
    for (const s of p.leadSources) {
      if (!creates('LEAD_SOURCE', s.name)) continue;
      const created = this.host.create(tenantId, 'LEAD_SOURCE', s, {});
      ids.set(k('LEAD_SOURCE', s.name), created.id);
      flag('LEAD_SOURCE', created);
    }
    for (const r of p.recurringPresets) {
      if (!creates('RECURRING_PRESET', r.name)) continue;
      const created = this.host.create(tenantId, 'RECURRING_PRESET', r, {});
      ids.set(k('RECURRING_PRESET', r.name), created.id);
      flag('RECURRING_PRESET', created);
    }

    const applied: types.StarterAppliedEntry[] = plan.map((e) => ({
      ...e,
      id: e.action !== 'skip' ? ids.get(k(e.entityType, e.name)) ?? null : null,
    }));
    const created = applied.filter((e) => e.action === 'create');
    const restored = applied.filter((e) => e.action === 'restore');
    if (created.length > 0 || restored.length > 0) {
      this.host.audit('starter.apply', { packKey: p.key, created: created.length, restored: restored.length, skipped: applied.filter((e) => e.action === 'skip').length });
    }
    return { packKey: p.key, created, restored, skipped: applied.filter((e) => e.action === 'skip') };
  }

  listSeeded(tenantId: string): types.SeededRecordsResponse {
    const packs: types.SeededPack[] = [];
    for (const { row, state } of this.live(tenantId)) {
      let group = packs.find((g) => g.packKey === row.packKey);
      if (!group) {
        group = { packKey: row.packKey, title: this.pack(row.packKey).title, records: [] };
        packs.push(group);
      }
      group.records.push({
        id: row.id,
        entityType: row.entityType,
        entityId: row.entityId,
        name: state.name,
        seededAt: row.seededAt,
        edited: state.updatedAt !== row.seededAt,
      });
    }
    return { packs };
  }

  keep(tenantId: string, id: string): types.StarterKeepResponse {
    const row = this.rows.find((r) => r.id === id && r.tenantId === tenantId);
    if (!row) throw new MockStarterError(404, 'Starter record not found', 'NOT_FOUND');
    this.rows = this.rows.filter((r) => r !== row);
    this.host.audit('starter.keep', { packKey: row.packKey, entityType: row.entityType, count: 1 });
    return { id: row.id, entityType: row.entityType, entityId: row.entityId };
  }

  removeUnused(tenantId: string, key: string): types.StarterRemoveResponse {
    const p = this.pack(key);
    const removed: types.StarterRemoveEntry[] = [];
    const kept: types.StarterRemoveResponse['kept'] = [];
    const live = this.live(tenantId, p.key);
    for (const type of REMOVE_ORDER) {
      for (const { row, state } of live.filter((x) => x.row.entityType === type)) {
        const entry = { entityType: type, name: state.name };
        if (state.updatedAt !== row.seededAt) kept.push({ ...entry, reason: 'EDITED' });
        else if (this.host.isUsed(tenantId, type, row.entityId)) kept.push({ ...entry, reason: 'IN_USE' });
        else {
          this.host.remove(tenantId, type, row.entityId);
          // A removed item keeps its flag row, marked, so re-applying the pack restores it
          if (type === 'ITEM') row.removedAt = new Date().toISOString();
          else this.rows = this.rows.filter((r) => r !== row);
          removed.push(entry);
        }
      }
    }
    // Flags whose record is gone are stale: drop them
    this.rows = this.rows.filter((r) => !(r.tenantId === tenantId && r.packKey === p.key && !r.removedAt && !this.host.state(tenantId, r.entityType, r.entityId)));
    if (removed.length > 0) this.host.audit('starter.remove', { packKey: p.key, removed: removed.length, kept: kept.length });
    return { packKey: p.key, removed, kept };
  }
}

